import { type SignInPhoneSchema, signInPhoneSchema } from '@coas/validators/payload';
import { useTranslations } from 'next-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { ZodErrorMap } from 'zod';
import { AuthButton } from '~/entities/AuthButton';
import { PhoneNumberInput } from '~/entities/PhoneNumberInput';
import { useSignInMutation } from '../model/mutations';

export const PhoneForm = () => {
    const { mutate, isPending } = useSignInMutation();
    const t = useTranslations();

    const errorMap: ZodErrorMap = useMemo(
        () => (issue, ctx) => {
            if (issue.code === 'too_small' && (issue.minimum as number) === 1) {
                return { message: t('auth.errors.phoneRequired') };
            }
            if (issue.code === 'custom') {
                return { message: t('auth.errors.phoneFormat') };
            }
            return { message: ctx.defaultError };
        },
        [t],
    );

    const { control, handleSubmit } = useForm<SignInPhoneSchema>({
        resolver: zodResolver(signInPhoneSchema, { errorMap }),
        defaultValues: { provider: 'phone', data: { phoneNumber: '' } },
        mode: 'onTouched',
    });

    const onSubmit = useCallback((values: SignInPhoneSchema) => mutate(values), [mutate]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <PhoneNumberInput name='data.phoneNumber' control={control} label={t('auth.phoneNumber')} disabled={isPending} />
            <AuthButton type='submit' isLoading={isPending}>
                {t('auth.loginButton')}
            </AuthButton>
        </form>
    );
};
